import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { IToggleComponent, ToasterService } from "@front/m19-ui";
import { CampaignType } from "@front/m19-api-client";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { StrategyEx } from "@front/m19-models";
import { StrategyService } from "@front/m19-injection-token";
import { SbStrategyEx } from "@front/m19-models/strategy/SbStrategyEx";
import { BaseStrategyService } from "@front/m19-services";
import { SpStrategyEx } from "@front/m19-models/strategy/SpStrategyEx";
import { SettingRowComponent } from "../setting-row/setting-row.component";

@Component({
  selector: "app-toggle-auto-segment",
  imports: [IToggleComponent, TranslocoDirective, SettingRowComponent],
  template: `@if (strategyEx(); as strategy) {
    <app-setting-row *transloco="let t">
      <ng-container label>
        @if (strategy.campaignType === CampaignType.SB) {
          {{ t("strategy-page.automatic_targeting_campaign_broad_match") }}
        } @else {
          {{ t("advanced-settings-modal.automatic_targeting_campaign") }}
        }
      </ng-container>

      <IToggle
        [disabled]="isReadOnly()"
        testid="target-campaign"
        [checked]="!strategy.disableAutoSegment"
        (checkedChange)="switchAutoTargetCampaign()"
        [loading]="isLoading()"
      />
    </app-setting-row>
  }`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleAutoSegmentComponent {
  private readonly strategyService = inject<BaseStrategyService<CampaignType.SB | CampaignType.SP>>(StrategyService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);

  protected readonly CampaignType = CampaignType;

  isReadOnly = input.required<boolean>();
  organizationId = input.required<number>();
  strategy = input.required<StrategyEx>();
  strategyEx = computed(() => {
    const strategy = this.strategy();
    return strategy.isSbStrategy() || strategy.isSpStrategy() ? strategy : undefined;
  });

  isLoading = signal(false);

  switchAutoTargetCampaign() {
    const strategy = this.strategyEx();
    if (!strategy) return;
    this.isLoading.set(true);
    this.strategyService
      .updateStrategyTargetCampaign(
        strategy.accountId,
        strategy.marketplace,
        this.organizationId(),
        strategy.strategyId,
        strategy.disableAutoSegment,
      )
      .subscribe({
        next: (strategy: SbStrategyEx | SpStrategyEx) => {
          this.toasterService.success(
            strategy.disableAutoSegment
              ? this.translocoService.translate("strategy-page.auto_campaign_disabled")
              : this.translocoService.translate("strategy-page.auto_campaign_enabled"),
            this.translocoService.translate("strategy-page.auto_campaign"),
          );
          this.isLoading.set(false);
        },
        error: (error) => {
          this.toasterService.error(error, this.translocoService.translate("strategy-page.auto_campaign_error"));
          this.isLoading.set(false);
        },
      });
  }
}
